// Assumed Equipment register — pure edit/payload helpers for the
// ProcessEquipment + EquipmentConnections model (db/24_process_equipment.sql),
// shared by the Assumed Equipment page (src/pages/estimate/AssumedEquipment.jsx)
// and its tests. P2-A (2026-09-12): data entry only — no load calculations
// (P2-B), no library copy/save (P2-C), no schedule (P2-D), no rollups (P2-E).
//
// sp_SaveProcessEquipment's contract, which everything below targets:
//   header fields written as sent (room_id / location_text verbatim so they can
//   be cleared); `connections[]` upserts — a row WITH connection_id updates,
//   one WITHOUT inserts; `deleted_connection_ids[]` removes rows (nulling any
//   EstimateCircuits.ConnectionID that pointed at them). Every call carries
//   revision_id, and the proc's UPDATE/DELETE are scoped to it — so a stale
//   row from another revision can never be touched through this path.

import { UTILITY_BY_KEY, isElectricalType } from "../config/utilityTypes";

const num = (v) => { if (v === null || v === undefined || v === "") return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
let keySeq = 0;
export const nextDraftKey = () => `new-${++keySeq}`;

// A blank connection row of a type, pre-filled with that type's default units.
export function newConnection(utilityType = "electrical") {
  const u = UTILITY_BY_KEY[utilityType] || {};
  return {
    ConnectionID: null, _key: nextDraftKey(), UtilityType: utilityType, CircuitDesc: "",
    FlowValue: null, FlowUnits: u.flowUnits ?? null, PressureValue: null, PressureUnits: u.pressureUnits ?? null,
    BranchSize: "", ConsumptionPerDay: null, ConsumptionUnits: null,
    Volts: "", Phase: null, FLA: null, MCA: null, MOCP: null, HP: null, KW: null, PowerClass: isElectricalType(utilityType) ? "normal" : null,
    UsageFactor: null, HeatGainBTUH: null, DiversityOn: true, AutoManual: null, Notes: "", DisplayOrder: 100,
  };
}

// Changing a row's type keeps the units the estimator already chose and
// fills in the new type's defaults only where nothing was set.
export function retypeConnection(conn, utilityType) {
  const u = UTILITY_BY_KEY[utilityType] || {};
  return { ...conn, UtilityType: utilityType, FlowUnits: conn.FlowUnits ?? u.flowUnits ?? null, PressureUnits: conn.PressureUnits ?? u.pressureUnits ?? null,
    PowerClass: isElectricalType(utilityType) ? (conn.PowerClass || "normal") : conn.PowerClass };
}

// Draft connection list editing. Existing rows are tracked by ConnectionID for
// deletion; a never-saved row just disappears.
export function draftConnectionsFrom(rows = []) {
  return rows.map(c => ({ ...c, _key: `c${c.ConnectionID}` }));
}
export function updateConnection(conns, key, patch) {
  return conns.map(c => c._key === key ? { ...c, ...patch } : c);
}
export function removeConnection(conns, deletedIds, key) {
  const row = conns.find(c => c._key === key);
  return {
    conns: conns.filter(c => c._key !== key),
    deletedIds: row?.ConnectionID ? [...deletedIds, row.ConnectionID] : deletedIds,
  };
}

// Room vs. location: a room assignment is authoritative; LocationText is only
// meaningful for equipment outside the takeoff rooms (roof, mech room). The
// payload therefore sends location_text only when no room is set, so the two
// can never both be stored.
export function chooseRoom(draft, roomId) {
  return { ...draft, RoomID: roomId ?? null };
}

// One connection row -> sp_SaveProcessEquipment `connections[]` entry.
export function connectionToPayload(c) {
  return {
    connection_id: c.ConnectionID ?? null,
    utility_type: c.UtilityType, circuit_desc: c.CircuitDesc || null,
    flow_value: num(c.FlowValue), flow_units: c.FlowUnits || null,
    pressure_value: num(c.PressureValue), pressure_units: c.PressureUnits || null,
    branch_size: c.BranchSize || null, consumption_per_day: num(c.ConsumptionPerDay), consumption_units: c.ConsumptionUnits || null,
    volts: c.Volts || null, phase: num(c.Phase), fla: num(c.FLA), mca: num(c.MCA), mocp: num(c.MOCP), hp: num(c.HP), kw: num(c.KW),
    power_class: c.PowerClass || null, usage_factor: num(c.UsageFactor), heat_gain_btuh: num(c.HeatGainBTUH),
    diversity_on: c.DiversityOn === false || c.DiversityOn === 0 ? 0 : 1, auto_manual: c.AutoManual || null,
    assembly_id: c.AssemblyID ?? null, route_lf: num(c.RouteLF),
    notes: c.Notes || null, display_order: num(c.DisplayOrder) ?? 100,
  };
}

// Full save payload for one equipment record (header + connections + deletions).
export function equipmentToPayload(draft, conns = [], deletedIds = [], revisionId) {
  if (revisionId == null) throw new Error("equipmentToPayload: revisionId is required");
  const roomId = draft.RoomID ?? null;
  return {
    equipment_id: draft.EquipmentID ?? null, revision_id: revisionId,
    tag: (draft.Tag || "").trim() || null,
    description: (draft.Description || "").trim() || "New Equipment",
    category: (draft.Category || "").trim() || null,
    manufacturer: (draft.Manufacturer || "").trim() || null, model: (draft.Model || "").trim() || null,
    qty: Math.max(1, num(draft.Qty) ?? 1), standby_units: Math.max(0, num(draft.StandbyUnits) ?? 0),
    room_id: roomId, location_text: roomId != null ? null : ((draft.LocationText || "").trim() || null),
    status: draft.Status || "new", furnished_by: draft.FurnishedBy || null, installed_by: draft.InstalledBy || null,
    library_item_id: draft.LibraryItemID ?? null, // provenance carried through unchanged (P2-C owns library workflows)
    // P2 Phase B: the project's own dimension snapshot + data provenance
    width_in: num(draft.WidthIn), depth_in: num(draft.DepthIn), height_in: num(draft.HeightIn), weight_lbs: num(draft.WeightLbs),
    assumption_status: draft.AssumptionStatus || "assumed",
    // P4-SCHED Release 2-A: procurement lead time (calendar weeks) + where it came from — quoted vs assumed is never blended
    lead_time_weeks: num(draft.LeadTimeWeeks), lead_time_source: draft.LeadTimeWeeks == null || draft.LeadTimeWeeks === "" ? null : (draft.LeadTimeSource || "estimator"), lead_time_quote_id: num(draft.LeadTimeQuoteID),
    // Phase H: the catalog Item/Assembly this record prices as (× installed Qty); exactly one or none
    cost_kind: draft.CostKind === "item" || draft.CostKind === "assembly" ? draft.CostKind : null,
    cost_item_id: draft.CostKind === "item" ? num(draft.CostItemID) : null,
    cost_assembly_id: draft.CostKind === "assembly" ? num(draft.CostAssemblyID) : null,
    notes: draft.Notes ?? null, display_order: num(draft.DisplayOrder) ?? 100,
    connections: conns.map(connectionToPayload),
    deleted_connection_ids: deletedIds,
  };
}

export function deletePayload(equipmentId, revisionId) {
  return { equipment_id: equipmentId, revision_id: revisionId, is_delete: 1 };
}

// ---- read helpers ------------------------------------------------------------------------

export const connectionsOf = (eq, conns = []) => conns.filter(c => c.EquipmentID === eq.EquipmentID);
export function connectionCounts(eq, conns = []) {
  const mine = connectionsOf(eq, conns);
  const electrical = mine.filter(c => isElectricalType(c.UtilityType)).length;
  return { electrical, utility: mine.length - electrical, total: mine.length };
}
// Display order, then tag (natural), then id — the register's stable order.
export function sortEquipment(list = []) {
  return [...list].sort((a, b) => (num(a.DisplayOrder) ?? 100) - (num(b.DisplayOrder) ?? 100)
    || String(a.Tag || "").localeCompare(String(b.Tag || ""), "en", { numeric: true })
    || (num(a.EquipmentID) ?? 0) - (num(b.EquipmentID) ?? 0));
}
// "101 — Fill" for a room-assigned record, else its location text, else null.
export function locationLabel(eq, roomsById) {
  if (eq.RoomID != null) {
    const r = roomsById?.get?.(eq.RoomID);
    return r ? `${r.RoomNumber ? `${r.RoomNumber} — ` : ""}${r.RoomName}` : `room #${eq.RoomID}`;
  }
  return eq.LocationText || null;
}
